import './scss/configurator-360.scss';
import f from 'facon';
import _bind from 'underscore/modules/bind';
import _debounce from 'underscore/modules/debounce';
import Controls from './controls';
import FullScreen from './fullscreen';
import ImageUtils from '../utils/image-utils';
// eslint-disable-next-line import/extensions
import 'pannellum/build/pannellum.js';
import 'pannellum/build/pannellum.css';

export default class Configurator360 {
    constructor(el) {
        this.el360 = el.querySelector('.preview-360');
        this.elPano = el.querySelector('.preview-pano');
        this.toggleInteriorViewBtn = el.querySelector('.interior-view');
        this.toggleExteriorViewBtn = el.querySelector('.exterior-view');
        this.toggleView = el.querySelectorAll('.toggle-view .view-btn-group button');
        this.config = { ...window?.HONDA?.Configurator360 };
        this.controls = new Controls(this.el360, this);
        this.fullScreen = new FullScreen(this.el360, this);
        this.params = new URLSearchParams();
        this.paths = {
            exterior: (params, frame) => `/gateway/api/car-images?mtoce=${encodeURIComponent(params.mtoce)}&angle=${frame}`,
            pano: (params) => `/gateway/api/car-images?type=interior&mtoce=${encodeURIComponent(params.mtoce)}`,
        };

        this.loader = document.querySelector('.bar-loader');
        this.loaderProgressBar = document.querySelector('.bar-loader-progress');
        this.dragger = `
            <div class="dragger">
                <svg class="dragger-icon" width="19" height="16" viewBox="0 0 19 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M2.12664 7.29345L6.98134 5.21937L4.8161 4.17094C5.91012 2.8718 7.55114 2.05128 9.37451 2.05128C12.3147 2.05128 14.7762 4.21652 15.2548 7.01994L17.1694 6.19943C16.3261 2.64387 13.158 0 9.35171 0C6.70784 0 4.36026 1.27635 2.90157 3.25926L0.736328 2.21083L2.12664 7.29345Z" fill="black"/>
                    <path d="M16.8964 8.15955L12.0417 10.2336L14.2753 11.3048C13.204 12.8775 11.3807 13.9259 9.3294 13.9259C6.20689 13.9259 3.65419 11.5328 3.40347 8.47863L1.44336 9.32194C2.10433 13.1282 5.40917 16 9.35219 16C12.224 16 14.7311 14.4957 16.1442 12.2165L18.2639 13.2422L16.8964 8.15955Z" fill="black"/>
                </svg>
                ${this.el360.dataset.labelDrag}
            </div>`;

        this.panoConfig = {
            type: 'equirectangular',
            panorama: '',
            autoLoad: true,
            minHfov: 50,
            maxHfov: 100,
            hfov: 80,
            showControls: false,
            mouseZoom: false,
        };
        this.init();
        this.model = '';
        this.exteriorStartTime = 0;
    }

    init() {
        this.bind();
        this.update();
    }

    bind() {
        window.addEventListener('hashchange', _debounce(_bind(this.update, this), 50));
        this.el360.addEventListener('pointerdown', () => {
            this.el360.querySelector('.dragger')?.remove();
        });
        this.elPano.addEventListener('pointerdown', () => {
            this.elPano.querySelector('.dragger')?.remove();
        });

        this.toggleView.forEach((element) => {
            element.addEventListener('click', (event) => {
                event.preventDefault();
                if (!event.target.closest('button').classList.contains('active')) {
                    if (event.target.closest('button').classList.contains('exterior-view')) {
                        this.showExterior();
                        this.exterior();
                    } else if (event.target.closest('button').classList.contains('interior-view')) {
                        this.showInterior();
                        this.interior();
                    }
                }
            });
        });
    }

    static isMobile() {
        return window.matchMedia('(max-width: 1024px)').matches;
    }

    update() {
        this.view = window.location.hash.split('?')[0] || '';
        this.query = window.location.hash.split('?')[1] || '';
        this.params = new URLSearchParams(this.query);
        this.config.angles = +this.params.get('angles') || window?.HONDA?.Configurator360.angles;
        this.config.startFrame = +this.params.get('startFrame') || window?.HONDA?.Configurator360.startFrame;

        const parts = window.location.pathname.split('/');
        if (parts.includes('cars') && parts.includes('new')) {
            const index = parts.indexOf('new');
            this.model = parts[index + 1];
        }

        this.defaultAnalytics = {
            event: '360 Configurator',
            eventAction: 'loaded',
            eventCategory: '360 Configurator',
            actionType: 'loaded',
            module: '360 Configurator',
            modelSelected: this.model,
        };

        if (this.view.endsWith('interior')) {
            this.stepNumber = 3;
            this.stepName = 'interior';
            this.showInterior();
            this.interior();
            this.controls.interior();
        } else {
            this.stepNumber = 2;
            this.stepName = 'exterior';
            this.controls.pause();
            this.showExterior();
            this.exterior();
        }
    }

    clear(el) {
        el.innerHTML = '';
    }

    showExterior() {
        this.el360.classList.remove('hide');
        this.elPano.classList.add('hide');
        this.toggleExteriorViewBtn.classList.add('active');
        this.toggleExteriorViewBtn.setAttribute('aria-selected', 'true');
        this.toggleInteriorViewBtn.classList.remove('active');
        this.toggleInteriorViewBtn.removeAttribute('aria-selected');
        this.analyticsPush('Exterior');
    }

    showInterior() {
        this.el360.classList.add('hide');
        this.elPano.classList.remove('hide');
        this.toggleInteriorViewBtn.classList.add('active');
        this.toggleInteriorViewBtn.setAttribute('aria-selected', 'true');
        this.toggleExteriorViewBtn.classList.remove('active');
        this.toggleExteriorViewBtn.removeAttribute('aria-selected');
        this.analyticsPush('Interior');
    }

    exterior() {
        this.clear(this.el360);
        this.showLoader();
        const mtoce = this.params.get('mtoce');

        if(!mtoce || mtoce === 'null') {
            return;
        }

        const baseParams = {
            mtoce,
        };

        let angles;
        if (this.config.angles > 1) {
            const currentFrame = this.controls.currentFrame ?? this.config.startFrame;
            angles = [...Array(this.config.angles).keys()];
            angles.push(...angles.splice(0, currentFrame - 1));
        } else {
            angles = [this.config.startFrame - 1];
        }

        const frames = angles.map((a) => this.frame(baseParams, a));
        const first = frames.shift();

        this.loaderProgress(frames);

        this.el360.appendChild(first.el);
        first.promise.then(() => {
            this.controls.showFirstFrame();
            frames.forEach((l) => this.el360.appendChild(l.el));
        });

        const all = Promise.all(frames.map((l) => l.promise));
        all.then(() => {
            this.hideLoader(this.el360);
            this.controls.exterior();
        });
    }

    loaderProgress(frames) {
        const numberOfFrames = frames.length;
        let numberOfLoaded = 0;
        frames.forEach((frame) => frame.promise.then(() => {
            numberOfLoaded += 1;
            this.loaderProgressBar.style.display = 'block';
            this.loaderProgressBar.style.width = `${(numberOfLoaded / numberOfFrames) * 100}%`;
        }));
    }

    interior() {
        this.clear(this.elPano);
        const self = this;
        if (!this.shownInteriorDragger) {
            this.shownInteriorDragger = true;
            this.elPano.insertAdjacentHTML('beforeend', this.dragger);
        }

        const mtoce = this.params.get('mtoce');

        if (mtoce) {
            const params = {
                mtoce,
            };
            const el = f`
                <div ref="frame" class="frame">
                    <div id="interior" class="layer interior"></div>
                </div>`;
            const path = this.paths.pano(params);
            const { frame } = el.collect();
            this.elPano.appendChild(frame);

            const interiorStartTime = (new Date()).getTime();

            // eslint-disable-next-line no-undef
            this.pano = pannellum.viewer('interior', {
                ...self.panoConfig,
                panorama: `${path}`,
            });

            this.updatePanoHfov();

            this.pano.on('load', () => {
                const loadTime = (new Date()).getTime() - interiorStartTime;
                const loadEvent = {
                    ...this.defaultAnalytics,
                    eventLabel: `${this.model}|${this.stepName}|${this.stepNumber}|${loadTime}`,
                    stepNumber: this.stepNumber,
                    stepName: this.stepName,
                };
                window.dataLayer.push(loadEvent);
            });
        }
    }

    updatePanoHfov() {
        const container = this.pano.getContainer();
        const ratio = container.offsetWidth / container.offsetHeight;
        const [minRatio, maxRatio] = [0.5, 1.4];
        const [minFov, maxFov] = this.pano.getHfovBounds();
        const ratioRange = (maxRatio - minRatio);
        const fovRange = (maxFov - minFov);
        if (this.pano) {
            const fov = (((ratio - minRatio) * fovRange) / ratioRange) + minFov;
            this.pano.setHfov(fov);
        }
    }

    showLoader() {
        this.exteriorStartTime = (new Date()).getTime();
        this.loader.classList.remove('hide');
    }

    hideLoader(wrapper) {
        this.loader.classList.add('hide');
        this.loadTime = (new Date()).getTime() - this.exteriorStartTime;
        const loadEvent = {
            ...this.defaultAnalytics,
            eventLabel: `${this.model}|${this.stepName}|${this.stepNumber}|${this.loadTime}`,
            stepNumber: this.stepNumber,
            stepName: this.stepName,
        };
        window.dataLayer.push(loadEvent);

        if (this.config.angles > 1 && !this.shownExteriorDragger && this.controls.isPan) {
            this.shownExteriorDragger = true;
            wrapper.insertAdjacentHTML('beforeend', this.dragger);
        }
    }

    frame(baseParams, angle) {
        const images = [
            this.image(this.paths.exterior(baseParams, this.pad(angle + 1))),
        ];

        const el = f`<div ref="frame" class="frame hide" data-frame="${angle + 1}"></div>`;
        const { frame } = el.collect();

        images.forEach((i) => frame.appendChild(i.el));

        if (this.el360.dataset.arrowsPath) {
            const promptArrows = f`<img src="${this.el360.dataset.arrowsPath}" />`;
            frame.appendChild(promptArrows);
        }

        return {
            el,
            promise: Promise.all(images.map((i) => i.promise)),
            images,
        };
    }

    pad(n) {
        return n.toString().padStart(3, '0');
    }

    image(path) {
        const el = f`
            <picture class="layer background">
                <source srcset="${path}" type="image/webp">
                <img ref="img" src="${path}">
            </picture>`;
        const { img } = el.collect();
        const promise = new Promise((resolve) => {
            img.onload = () => resolve(img);
            img.onerror = () => resolve(img);
        });

        return {
            el,
            promise,
        };
    }

    analyticsPush(text) {
        const model = this.params.get('model');
        window.dataLayer.push({
            event: '360 Configurator',
            eventCategory: 'Car Configurator',
            eventAction: `loaded-${text.toLowerCase()}`,
            eventLabel: `cars|${model}|configurator|Step ${this.stepNumber}. ${this.stepName}|${text}|internal|middle|#`,
            actionType: `loaded-${text.toLowerCase()}`,
            businessUnit: 'cars',
            moduleName: 'configurator',
            clickText: `${text}`,
            clickType: 'internal',
            destinationURL: '#',
            configuratorStepName: `Step ${this.stepNumber}. ${this.stepName}`,
            modelSelected: `${model}`,
            inPageLocation: 'middle',
        });
    }
}

window.HONDA.Configurator360.instance = new Configurator360(document.querySelector('.configurator-360'));
